import { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Support() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !message) {
      toast.error('Please fill in email and message');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/support`, {
        name: name || undefined,
        email,
        message
      });

      setSuccess(true);
      toast.success('Support request sent successfully!');
      setName('');
      setEmail('');
      setMessage('');
    } catch (error) {
      console.error('Failed to send support request:', error);
      toast.error('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-4">
              <img 
                src="/logo.png" 
                alt="PetFlyCheck Logo" 
                className="w-20 h-20 md:w-24 md:h-24 object-contain"
              />
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900">PetFlyCheck</h1>
            </a>
            <nav className="flex gap-8">
              <a href="/faq" className="text-lg font-medium text-slate-600 hover:text-slate-900">FAQ</a>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Contact Support</h2>
          <p className="text-slate-600">We're here to help. Send us your question or concern.</p>
          <p className="text-sm text-slate-500 mt-2">
            Email: <a 
              href="mailto:contact@petflycheck.io?subject=Support Request - PetFlyCheck"
              className="text-[#0066CC] hover:underline"
            >
              contact@petflycheck.io
            </a>
          </p>
        </div>

        {success ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Request Sent!</h3>
              <p className="text-slate-600 mb-6">We've received your message and will respond within 24 hours.</p>
              <Button
                onClick={() => setSuccess(false)}
                variant="outline"
                className="rounded-full"
              >
                Send Another Message
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Name (Optional)
                  </Label>
                  <Input
                    id="name"
                    data-testid="support-name-input"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 rounded-lg border-slate-200"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    data-testid="support-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 rounded-lg border-slate-200"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="support-message-input"
                    placeholder="Describe your issue or question..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={6}
                    className="rounded-lg border-slate-200 resize-none"
                  />
                </div>

                <Button
                  data-testid="support-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full py-6"
                >
                  {loading ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-slate-400">
            <p>© 2026 PetFlyCheck. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
